import React, { useContext, useEffect, useState } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import { UserContext } from '../../context/UserContextProvider';

/**
 * AvailableTestsOptionalModules
 * -----------------------------------------
 * A collapsible panel that surfaces the same list of
 * tests/modules that the Header dropdown shows.  The
 * current test (identified by `accessCode`) is rendered
 * as plain text (non-clickable) while the others are
 * interactive links that will call `switchTest()`.
 */
const AvailableTestsOptionalModules = () => {
  const {
    accessCode,
    getApiConfig,
    switchTest,
  } = useContext(UserContext);

  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch the list of active tests — mirrors the logic in Header.jsx
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await fetch(getApiConfig('getActiveTestsBySiteAccessCode'), {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            'access-code': accessCode,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setTests(data);
      } catch (err) {
        console.error('Error fetching available tests:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (accessCode) {
      fetchTests();
    } else {
      setIsLoading(false);
    }
  }, [accessCode, getApiConfig]);

  const extractDisplayName = (rawName) => {
    const regex = /^[a-zA-Z]+\s\d+\s(.*?)\sTemplate/;
    const match = rawName?.match(regex);
    return match ? match[1] : rawName;
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center p-3">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (!tests || tests.length === 0) {
      return (
        <Card.Body>
          <em>No tests available</em>
        </Card.Body>
      );
    }

    return (
      <ListGroup variant="flush">
        {tests.map((test) => {
          const isCurrent = test.access_code === accessCode;

          return (
            <ListGroup.Item
              key={test.access_code}
              className="d-flex align-items-center"
              style={{
                opacity: isCurrent ? 0.6 : 1,
              }}
            >
              {isCurrent ? (
                <span>{extractDisplayName(test.test_name)}{test.role_display_name ? ` (${test.role_display_name})` : ''}</span>
              ) : (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    switchTest(test.access_code);
                  }}
                  className="text-primary"
                  style={{ textDecoration: 'none' }}
                >
                  {extractDisplayName(test.test_name)}{test.role_display_name ? ` (${test.role_display_name})` : ''}
                </a>
              )}
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    );
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header
        as="h6"
        className="d-flex align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        <span className="ml-2">Available Tests / Optional Modules</span>
      </Card.Header>
      <Collapse in={isOpen}>
        <div>{renderBody()}</div>
      </Collapse>
    </Card>
  );
};

export default AvailableTestsOptionalModules;
